import {useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {TalentPoolSummary} from './types/TalentPoolTypes';
import {quotaToPercent} from './utils/talentPoolQuotaUtils';
import {BaseStatCard} from '../../../../components/BaseStatCard/BaseStatCard';
import {StatsGridContainer} from '../../../../components/StatsGridContainer/StatsGridContainer';
import {greenColors} from '../../../../layouts/theme/designSystem';

interface Props {
  summary?: TalentPoolSummary | null;
  loading?: boolean;
}

export const TalentPoolCountDisplay = ({summary, loading}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'talentPoolPage.stats'});

  const metrics = useMemo(() => {
    const testsTaken = summary?.testsTaken ?? 0;
    const testsPassed = summary?.testsPassed ?? 0;
    const applicants = summary?.applicants ?? 0;
    const matches = summary?.matches ?? 0;
    const matchRatePercent = summary != null ? quotaToPercent(summary.matchingQuota) : 0;
    const testsPassedQuotaPercent = summary != null ? quotaToPercent(summary.testsPassedQuota ?? 0) : 0;
    const applicantsQuotaPercent = summary != null ? quotaToPercent(summary.applicantsQuota ?? 0) : 0;

    return {
      testsTaken,
      testsPassed,
      applicants,
      matches,
      matchRatePercent,
      testsPassedQuotaPercent,
      applicantsQuotaPercent,
    };
  }, [summary]);

  if (loading) {
    return <StatsGridContainer loading />;
  }

  return (
    <StatsGridContainer>
      <BaseStatCard
        fillWidth
        title={t('testCompletedTitle')}
        value={metrics.testsTaken}
        subtitle={t('testCompletedSubtitle')}
        valueSx={{color: greenColors[700], fontWeight: 700}}
      />
      <BaseStatCard
        fillWidth
        title={t('testPassedTitle')}
        value={metrics.testsPassed}
        subtitle={t('testPassedSubtitle', {percent: metrics.testsPassedQuotaPercent})}
        valueSx={{color: greenColors[500], fontWeight: 600}}
      />
      <BaseStatCard
        fillWidth
        title={t('applicationsTitle')}
        value={metrics.applicants}
        subtitle={t('applicationsSubtitle', {percent: metrics.applicantsQuotaPercent})}
        valueSx={{color: greenColors[600], fontWeight: 600}}
      />
      <BaseStatCard
        fillWidth
        title={t('topMatchesTitle')}
        value={metrics.matches}
        subtitle={t('topMatchesSubtitle', {percent: metrics.matchRatePercent})}
        valueSx={{color: greenColors[100], fontWeight: 600}}
      />
    </StatsGridContainer>
  );
};
